import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Sheet, FileSpreadsheet, ClipboardList } from "lucide-react";
import { buildingsApi, doorsApi, inspectionsApi, exportsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError, fmtDate } from "@/lib/format";
import { downloadBase64Envelope } from "@/lib/download";
import {
  buildRemedialSchedule, latestInspectionPerDoor, indexTracking, trackingFor,
  trackingKey, TRACKING_STATUS_LABEL, TRACKING_STATUS_ORDER, remedialScheduleExportRows,
} from "@/pdf/remedialSchedule";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const RISK_TONE = {
  Critical: "bg-red-100 text-red-700",
  High: "bg-orange-100 text-orange-700",
  Medium: "bg-amber-100 text-amber-700",
  Low: "bg-slate-100 text-slate-600",
};
const STATUS_TONE = {
  open: "bg-slate-100 text-slate-600",
  assigned: "bg-sky-100 text-sky-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
};

export default function RemedialSchedule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const building = useAsync(() => buildingsApi.getOne(id), [id]);
  const doors = useAsync(() => doorsApi.getAll(id), [id]);
  const inspections = useAsync(() => inspectionsApi.getAll({ building_id: id }), [id]);
  const tracking = useAsync(() => buildingsApi.remedialTracking(id), [id]);
  const [saving, setSaving] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [edits, setEdits] = useState({});

  const loading = building.loading || doors.loading || inspections.loading || tracking.loading;
  const error = building.error || doors.error || inspections.error;

  const actions = useMemo(() => {
    if (loading) return [];
    const latest = Array.from(latestInspectionPerDoor(inspections.data || []).values());
    return buildRemedialSchedule(doors.data || [], latest).remedialActions || [];
  }, [loading, doors.data, inspections.data]);

  const trackingRows = useMemo(() => {
    const d = tracking.data;
    return Array.isArray(d) ? d : (d?.items || []);
  }, [tracking.data]);
  const idx = useMemo(() => indexTracking(trackingRows), [trackingRows]);
  const rowFor = (a) => {
    const k = trackingKey(a.doorId, a.defectKey);
    return edits[k] || trackingFor(idx, a) || { status: "open", assigned_to: "", date_completed: "", notes: "" };
  };

  const patch = async (a, data) => {
    const k = trackingKey(a.doorId, a.defectKey);
    const merged = { ...rowFor(a), ...data };
    setEdits((p) => ({ ...p, [k]: merged }));
    setSaving(k);
    try {
      await buildingsApi.upsertRemedialTracking(id, a.doorId, a.defectKey, {
        status: merged.status, assigned_to: merged.assigned_to, date_completed: merged.date_completed, notes: merged.notes,
      });
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setSaving(null); }
  };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const tr = await buildingsApi.remedialTracking(id);
      const trRows = Array.isArray(tr.data) ? tr.data : (tr.data?.items || []);
      const rows = remedialScheduleExportRows(doors.data || [], inspections.data || [], trRows);
      const r = await exportsApi.remedialSchedule(id, rows, fmt);
      downloadBase64Envelope(r.data, `remedial_schedule.${fmt}`);
      toast.success(`Exported ${rows.length} lines`);
    } catch (e) { toast.error(apiError(e, "Export failed")); }
    finally { setExporting(false); }
  };

  const done = actions.filter((a) => rowFor(a).status === "completed").length;

  return (
    <div className="space-y-6 pb-10">
      <button onClick={() => navigate(`/buildings/${id}`)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="remedial-back">
        <ArrowLeft className="h-4 w-4" /> Back to building
      </button>
      <PageHeader
        title="Remedial Action Schedule"
        subtitle={building.data ? building.data.name : "Track and export outstanding remedial works"}
        testId="remedial-schedule-header"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => doExport("csv")} disabled={exporting || !actions.length} data-testid="remedial-export-csv">
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} CSV
            </Button>
            <Button onClick={() => doExport("xlsx")} disabled={exporting || !actions.length} data-testid="remedial-export-xlsx">
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sheet className="mr-2 h-4 w-4" />} Excel
            </Button>
          </div>
        }
      />

      {loading && <LoadingState label="Building remedial schedule…" rows={5} />}
      {error && <ErrorState message={apiError(error)} onRetry={() => { doors.reload(); inspections.reload(); tracking.reload(); }} />}

      {!loading && !error && (
        actions.length === 0 ? (
          <EmptyState title="No outstanding remedials" message="Every inspected door in this building has no outstanding remedials — nothing to schedule." />
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="remedial-summary">
              <ClipboardList className="h-4 w-4" /> {actions.length} remedial lines · {done} completed
            </div>
            <div className="space-y-3">
              {actions.map((a, i) => {
                const row = rowFor(a);
                const k = trackingKey(a.doorId, a.defectKey);
                return (
                  <Card key={k + i} className="p-4" data-testid={`remedial-row-${i}`}>
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800">{a.doorRef}{a.location ? <span className="font-normal text-slate-400"> · {a.location}</span> : null}</p>
                        <p className="text-sm text-slate-600">{a.defect}</p>
                        <p className="mt-0.5 text-xs text-slate-400">{a.action} · Target: {a.timescale}{a.inspectionDate ? ` · Inspected ${fmtDate(a.inspectionDate)}` : ""}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${RISK_TONE[a.risk] || RISK_TONE.Low}`}>{a.risk}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                      <div>
                        <Select value={row.status || "open"} onValueChange={(v) => patch(a, { status: v })}>
                          <SelectTrigger data-testid={`remedial-status-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TRACKING_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{TRACKING_STATUS_LABEL[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input placeholder="Assigned to" defaultValue={row.assigned_to} onBlur={(e) => e.target.value !== row.assigned_to && patch(a, { assigned_to: e.target.value })} data-testid={`remedial-assigned-${i}`} />
                      <Input type="date" defaultValue={(row.date_completed || "").slice(0, 10)} onChange={(e) => patch(a, { date_completed: e.target.value })} data-testid={`remedial-date-${i}`} />
                      <Input placeholder="Notes" defaultValue={row.notes} onBlur={(e) => e.target.value !== row.notes && patch(a, { notes: e.target.value })} data-testid={`remedial-notes-${i}`} />
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status || "open"]}`}>{TRACKING_STATUS_LABEL[row.status || "open"]}</span>
                      {saving === k && <span className="flex items-center gap-1 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>}
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )
      )}
    </div>
  );
}
